import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer, Search as SearchIcon, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to convert number to words
const numberToWords = (num) => {
  if (num === 0) return 'Zero Rupees Only.';
  
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  
  const convertLessThanThousand = (n) => {
    if (n === 0) return '';
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
  };
  
  const convertToWords = (n) => {
    if (n === 0) return '';
    
    let crore = Math.floor(n / 10000000);
    let lakh = Math.floor((n % 10000000) / 100000);
    let thousand = Math.floor((n % 100000) / 1000);
    let remainder = n % 1000;
    
    let result = '';
    
    if (crore > 0) result += convertLessThanThousand(crore) + ' Crore ';
    if (lakh > 0) result += convertLessThanThousand(lakh) + ' Lakh ';
    if (thousand > 0) result += convertLessThanThousand(thousand) + ' Thousand ';
    if (remainder > 0) result += convertLessThanThousand(remainder);
    
    return result.trim();
  };
  
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  
  let result = convertToWords(rupees);
  if (result) {
    result += ' Rupees';
    if (paise > 0) result += ' and ' + convertToWords(paise) + ' Paise';
    result += ' Only.';
  }
  
  return result;
};

const PrintBill = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const printType = searchParams.get('type') || 'bill'; // 'bill' or 'pi'
  const isPIMode = printType === 'pi';
  const [loading, setLoading] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState({
    fy: '2026-27',
    magazine: '',
    bill_no: '',
    entity_id: '',
    company_name: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [selectedBills, setSelectedBills] = useState([]);
  const [withLetterhead, setWithLetterhead] = useState(false);
  const [withSignature, setWithSignature] = useState(false);
  const [userRole, setUserRole] = useState(null);

  // Get auth token and user role
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    
    if (!token) {
      toast.error('Please login to view this page');
      navigate('/login');
      return;
    }
    
    setUserRole(role);
  }, [navigate]);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const searchPayload = {
        ...searchCriteria,
        document_type: isPIMode ? 'Proforma Invoice' : 'Bill'
      };
      const response = await axios.post(`${API}/bills/search`, searchPayload);
      
      if (response.data.length === 0) {
        toast.info(isPIMode ? 'No Proforma Invoices found' : 'No bills found matching the criteria');
        setSearchResults([]);
      } else {
        setSearchResults(response.data);
        toast.success(`Found ${response.data.length} bill(s)`);
      }
    } catch (error) {
      console.error('Error searching bills:', error);
      toast.error('Failed to search bills');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSearchCriteria({
      magazine: '',
      bill_no: '',
      entity_id: '',
      company_name: ''
    });
    setSearchResults([]);
    setSelectedBills([]);
    setWithLetterhead(false);
    setWithSignature(false);
  };

  const handleSelectBill = (bill) => {
    setSelectedBills([bill]);
  };

  const handlePrint = () => {
    // Set document title for PDF filename
    if (selectedBills.length === 1) {
      const bill = selectedBills[0];
      const prefix = (bill.magazine === 'Aabkari Times' || bill.magazine === 'Aabkari Times ENG') ? 'AT' : 'ST';
      if (bill.document_type === 'Proforma Invoice') {
        document.title = `${prefix}PI-${bill.bill_no}`;
      } else {
        document.title = `${prefix}-${bill.bill_no}`;
      }
    } else {
      document.title = 'Bills';
    }
    window.print();
    // Reset title after print
    setTimeout(() => { document.title = 'Magazine CRM'; }, 1000);
  };

  const handleSaveAsPDF = () => {
    // Set document title for PDF filename
    if (selectedBills.length === 1) {
      const bill = selectedBills[0];
      const prefix = (bill.magazine === 'Aabkari Times' || bill.magazine === 'Aabkari Times ENG') ? 'AT' : 'ST';
      if (bill.document_type === 'Proforma Invoice') {
        document.title = `${prefix}PI-${bill.bill_no}`;
      } else {
        document.title = `${prefix}-${bill.bill_no}`;
      }
    } else {
      document.title = 'Bills';
    }
    window.print();
    // Reset title after print
    setTimeout(() => { document.title = 'Magazine CRM'; }, 1000);
  };

  const aabkariLetterheadUrl = 'https://customer-assets.emergentagent.com/job_magbilling-fix/artifacts/cnaoj1ml_i1q7qs2w_AT-new-letterhead%20%281%29gst.jpg';
  const aabkariSignatureUrl = 'https://customer-assets.emergentagent.com/job_magbilling-fix/artifacts/r0lwb43e_ygyj3jbk_aabkari%20times_stamp.png';
  const sugarLetterheadUrl = 'https://customer-assets.emergentagent.com/job_magbilling-fix/artifacts/9kpvxf4i_tlmog5jh_ST%20Letter%20head.jpg';
  const sugarSignatureUrl = 'https://customer-assets.emergentagent.com/job_magbilling-fix/artifacts/hpgfnuly_ri36tyna_sugar%20times_stamp.png';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8">
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .bill-content, .bill-content * {
              visibility: visible;
            }
            .bill-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 210mm !important;
              height: 297mm !important;
              max-width: 210mm;
              max-height: 297mm;
              padding: 8mm 10mm !important;
              margin: 0 !important;
              box-sizing: border-box;
              overflow: hidden;
            }
            .no-print {
              display: none !important;
            }
            @page {
              size: A4 portrait;
              margin: 0;
            }
            
            /* Optimize spacing for print */
            .bill-content .header {
              min-height: 120px !important;
              margin-bottom: 8px !important;
            }
            .bill-content .invoice-header {
              padding-top: 6px !important;
              padding-bottom: 6px !important;
              margin-bottom: 8px !important;
            }
            .bill-content p {
              margin-bottom: 4px !important;
            }
            .bill-content table {
              margin-bottom: 8px !important;
            }
            .bill-content .footer {
              margin-top: 10px !important;
            }
            .bill-content .footer p,
            .bill-content .footer li {
              margin-bottom: 2px !important;
              line-height: 1.3 !important;
            }
            .bill-content .footer ul {
              margin-top: 4px !important;
            }
            
            /* Reduce font sizes slightly for print */
            .bill-content {
              font-size: 10px !important;
            }
            .bill-content table {
              font-size: 10px !important;
            }
            .bill-content .footer {
              font-size: 9px !important;
            }
          }
          
          .bill-content {
            max-width: 210mm;
            margin: 0 auto;
            background: white;
            padding: 20px;
          }
          
          .header-text-hidden {
            visibility: hidden;
            opacity: 0;
          }
        `}
      </style>

      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8 no-print">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }}>
              {isPIMode ? 'Print Proforma Invoices' : 'Print Bills'}
            </h1>
          </div>
        </div>

        {selectedBills.length === 0 && (
          <div className="form-section mb-8 no-print">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#7c3aed' }}>
              Search Bills
            </h2>
            <form onSubmit={handleSearch}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div className="space-y-2">
                  <Label>Financial Year *</Label>
                  <Select value={searchCriteria.fy} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, fy: value })}>
                    <SelectTrigger data-testid="fy-filter">
                      <SelectValue placeholder="Select FY" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2026-27">2026-27</SelectItem>
                      <SelectItem value="2025-26">2025-26</SelectItem>
                      <SelectItem value="2024-25">2024-25</SelectItem>
                      <SelectItem value="2023-24">2023-24</SelectItem>
                      <SelectItem value="2022-23">2022-23</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Magazine</Label>
                  <Select value={searchCriteria.magazine} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, magazine: value })}>
                    <SelectTrigger data-testid="magazine-select">
                      <SelectValue placeholder="Select magazine" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                      <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Bill No</Label>
                  <Input data-testid="bill-no-input" value={searchCriteria.bill_no} onChange={(e) => setSearchCriteria({ ...searchCriteria, bill_no: e.target.value })} placeholder="Enter bill number" />
                </div>

                <div className="space-y-2">
                  <Label>ID</Label>
                  <Input data-testid="entity-id-input" value={searchCriteria.entity_id} onChange={(e) => setSearchCriteria({ ...searchCriteria, entity_id: e.target.value })} placeholder="Enter member/advertiser ID" />
                </div>

                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input data-testid="company-name-input" value={searchCriteria.company_name} onChange={(e) => setSearchCriteria({ ...searchCriteria, company_name: e.target.value })} placeholder="Enter company name" />
                </div>
              </div>

              <div className="flex space-x-4">
                <Button type="submit" disabled={loading} className="btn-primary" style={{ backgroundColor: '#7c3aed' }} data-testid="search-button">
                  <SearchIcon className="mr-2" size={18} />
                  {loading ? 'Searching...' : 'SEARCH'}
                </Button>
                <Button type="button" onClick={handleReset} variant="outline" data-testid="reset-button">
                  Reset
                </Button>
              </div>
            </form>
          </div>
        )}

        {searchResults.length > 0 && selectedBills.length === 0 && (
          <div className="form-section mb-8 no-print">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#7c3aed' }}>
              Select Bills to Print
            </h2>
            <div className="space-y-2 mb-6">
              {searchResults.map((bill) => (
                <div
                  key={bill.id}
                  className="flex items-center space-x-4 p-4 border rounded-lg cursor-pointer hover:bg-purple-50 transition"
                  onClick={() => handleSelectBill(bill)}
                  data-testid={`bill-option-${bill.bill_no}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedBills.find((b) => b.id === bill.id) !== undefined}
                    onChange={() => {}}
                    className="cursor-pointer"
                  />
                  <div className="flex-1">
                    <p className="font-semibold">Bill No: {String(bill.bill_no).replace('.0', '')}</p>
                    <p className="text-sm text-gray-600">{bill.magazine} - {bill.billing_name}</p>
                  </div>
                  <span className="text-lg font-bold">₹ {bill.total_amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedBills.length > 0 && (
          <div>
            <div className="flex space-x-4 mb-6 no-print">
              <Button onClick={handlePrint} className="btn-primary" style={{ backgroundColor: '#7c3aed' }} data-testid="print-button">
                <Printer className="mr-2" size={18} />
                PRINT
              </Button>
              <Button onClick={handleSaveAsPDF} style={{ backgroundColor: '#059669', color: 'white' }} data-testid="save-pdf-button">
                <Download className="mr-2" size={18} />
                SAVE AS PDF
              </Button>
              <Button onClick={() => setSelectedBills([])} variant="outline" data-testid="back-to-search-button">
                Back to Search
              </Button>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="letterhead"
                  checked={withLetterhead}
                  onChange={(e) => setWithLetterhead(e.target.checked)}
                  className="cursor-pointer"
                />
                <Label htmlFor="letterhead" className="cursor-pointer">With Letterhead</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="signature"
                  checked={withSignature}
                  onChange={(e) => setWithSignature(e.target.checked)}
                  className="cursor-pointer"
                />
                <Label htmlFor="signature" className="cursor-pointer">With Signature</Label>
              </div>
            </div>

            {selectedBills.map((bill, index) => (
              <div key={index} className="mb-8" style={{ pageBreakAfter: index < selectedBills.length - 1 ? 'always' : 'auto' }}>
                {(bill.magazine === 'Aabkari Times' || bill.magazine === 'Aabkari Times ENG') ? (
                  <div className="bill-content" style={{ position: 'relative', zIndex: 1 }}>
                    <div className="header mb-2" style={{ minHeight: '150px' }}>
                      {withLetterhead && (
                        <img src={aabkariLetterheadUrl} alt="Aabkari Times Letterhead" style={{ width: '100%', maxHeight: '150px', objectFit: 'contain' }} />
                      )}
                    </div>
                    
                    <div className="invoice-header text-center py-1 mb-2" style={{ borderTop: '2px solid #7c3aed', borderBottom: '2px solid #7c3aed' }}>
                      <h2 className="text-base font-bold">
                        {bill.document_type === 'Proforma Invoice' ? 'PROFORMA INVOICE' : 
                         bill.document_type === 'Receipt' ? 'RECEIPT' : 
                         (bill.product === 'Advertisement' || bill.product === 'Subscription/Advertisement') ? 'GST TAX INVOICE' :
                         'INVOICE'}
                      </h2>
                    </div>
                    
                    {(bill.product === 'Advertisement' || bill.product === 'Subscription/Advertisement') && (
                    <div className="mb-2" style={{ fontSize: '10px' }}>
                      <p className="mb-0.5"><strong>GSTIN:</strong> 09AIBPM4779R1ZA</p>
                      <p><strong>Email:</strong> info@aabkaritimes.com</p>
                    </div>
                    )}
                    
                    <div className="flex justify-end mb-2" style={{ fontSize: '10px' }}>
                      <div className="text-right">
                        <p className="mb-1">Date: <strong>{bill.bill_date ? new Date(bill.bill_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : new Date(bill.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong></p>
                        <p>{bill.document_type === 'Proforma Invoice' ? 'Proforma Invoice No' : 'Bill No'}: <strong>{bill.document_type === 'Proforma Invoice' ? (bill.proforma_invoice_no || bill.bill_no) : String(bill.bill_no).replace('.0', '')}</strong></p>
                        {bill.po_wo_no && <p className="mt-1">P.O./W.O. No: <strong>{bill.po_wo_no}</strong></p>}
                        {bill.document_type !== 'Proforma Invoice' && bill.proforma_invoice_no && <p className="mt-1">Proforma Invoice No: <strong>{bill.proforma_invoice_no}</strong></p>}
                      </div>
                    </div>
                    
                    <div className="mb-2" style={{ fontSize: '10px' }}>
                      <p className="mb-0.5"><strong>To,</strong></p>
                      <p className="font-semibold" style={{ fontSize: '11px' }}><strong>{bill.initial} {bill.bill_receipt_name || bill.billing_name}</strong></p>
                      {bill.billing_address && <p>{bill.billing_address}</p>}
                      <p>{bill.district ? `${bill.district}` : ''}{bill.pincode ? `-${String(bill.pincode).replace('.0', '')}` : ''}{bill.billing_state ? ` (${bill.billing_state})` : ''}</p>
                      {bill.gst_no && <p className="mt-0.5"><strong>GST No.:</strong> {bill.gst_no}</p>}
                    </div>
                    
                    <table className="w-full border-collapse mb-2" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#7c3aed', color: 'white' }}>
                          <th className="p-1.5 text-left" style={{ width: '40px', border: '1px solid #6b7280', fontSize: '10px' }}>S.No</th>
                          <th className="p-1.5 text-left" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>Item</th>
                          <th className="p-1.5 text-right" style={{ width: '120px', border: '1px solid #6b7280', fontSize: '10px' }}>Amount(in Rs.)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="p-1.5 align-top" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>1.</td>
                          <td className="p-1.5" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>
                            <p className="mb-0.5">
                              {bill.item_description || `Towards your ${bill.product === 'Renewal' ? 'Subscription Renewal' : bill.product || 'Subscription'} charges of ${bill.magazine} magazine for ${bill.period_duration || bill.advertisement_tenure || '1 Month'}${bill.occasion && bill.occasion !== 'None' ? ` in occasion ${bill.occasion}` : ''}`}
                              {bill.include_in_aabkari && bill.magazine === 'Sugar Times' && <span className="font-semibold"> + Aabkari Times (Complimentary)</span>}
                            </p>
                            <p className="mt-0.5 mb-0.5" style={{ fontSize: '9px' }}><strong>HSN Code:</strong> 998363</p>
                            <p className="mt-1 mb-0.5"><em style={{ fontSize: '9px' }}>Details-</em></p>
                            <p className="ml-2" style={{ fontSize: '9px' }}>Type- {bill.advertisement_position || bill.product}</p>
                            {bill.advertisement_tenure && <p className="ml-2" style={{ fontSize: '9px' }}>Period- {bill.advertisement_tenure}</p>}
                            {bill.period_duration && <p className="ml-2" style={{ fontSize: '9px' }}>Duration- {bill.period_duration}</p>}
                            {bill.start_month && bill.start_year && <p className="ml-2" style={{ fontSize: '9px' }}>Starts from- {bill.start_month} {bill.start_year}</p>}
                            {bill.end_month && bill.end_year && <p className="ml-2" style={{ fontSize: '9px' }}>Ends on- {bill.end_month} {bill.end_year}</p>}
                            
                            {/* GST only for Aabkari Times Advertisers - ignore stored GST values for other types */}
                            {(bill.magazine === 'Aabkari Times' || bill.magazine === 'Aabkari Times ENG') && bill.type === 'Advertiser' && ((bill.cgst_amount && bill.cgst_amount > 0) || (bill.sgst_amount && bill.sgst_amount > 0) || (bill.igst_amount && bill.igst_amount > 0)) && (
                              <table className="mt-1.5 w-full" style={{ fontSize: '9px' }}>
                                <tbody>
                                  {(bill.cgst_amount > 0 || bill.sgst_amount > 0) && (
                                    <>
                                      <tr>
                                        <td className="font-semibold">SGST (2.5%)</td>
                                        <td className="text-right font-semibold">₹ {(bill.sgst_amount || 0).toFixed(2)}</td>
                                      </tr>
                                      <tr>
                                        <td className="font-semibold">CGST (2.5%)</td>
                                        <td className="text-right font-semibold">₹ {(bill.cgst_amount || 0).toFixed(2)}</td>
                                      </tr>
                                    </>
                                  )}
                                  {bill.igst_amount > 0 && (
                                    <tr>
                                      <td className="font-semibold">IGST (5%)</td>
                                      <td className="text-right font-semibold">₹ {(bill.igst_amount || 0).toFixed(2)}</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            )}
                          </td>
                          <td className="p-1.5 align-top" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>
                            <p className="text-right font-semibold">{bill.gross_amount.toFixed(2)}</p>
                          </td>
                        </tr>
                        {/* Discount Row - Only shown if discount exists */}
                        {bill.discount_amount > 0 && (
                          <tr>
                            <td colSpan="2" className="p-1.5 text-right" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>
                              <span className="font-semibold">Less: Discount {bill.discount_percentage > 0 ? `(${bill.discount_percentage}%)` : ''}</span>
                            </td>
                            <td className="p-1.5 text-right font-semibold" style={{ border: '1px solid #6b7280', fontSize: '10px', color: '#dc2626' }}>
                              - {bill.discount_amount.toFixed(2)}
                            </td>
                          </tr>
                        )}
                        {/* Net Amount Row - Only shown if discount exists */}
                        {bill.discount_amount > 0 && (
                          <tr>
                            <td colSpan="2" className="p-1.5 text-right" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>
                              <span className="font-semibold">Net Amount</span>
                            </td>
                            <td className="p-1.5 text-right font-semibold" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>
                              {(bill.gross_amount - (bill.discount_amount || 0)).toFixed(2)}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td colSpan="2" className="p-1.5 text-right font-bold" style={{ border: '1px solid #6b7280', fontSize: '11px' }}>Total</td>
                          <td className="p-1.5 text-right font-bold" style={{ border: '1px solid #6b7280', fontSize: '12px' }}>₹ {(bill.total_amount || bill.gross_amount).toFixed(2)} /-</td>
                        </tr>
                      </tbody>
                    </table>
                    
                    <p className="mb-2" style={{ fontSize: '10px' }}><strong>In words:</strong> <span className="italic">{numberToWords((bill.magazine === 'Aabkari Times' || bill.magazine === 'Aabkari Times ENG') && bill.type === 'Advertiser' ? bill.total_amount : bill.gross_amount)}</span></p>
                    
                    <div className="footer mt-2" style={{ fontSize: '9px' }}>
                      <div className="flex justify-between">
                        <div className="w-1/2 pr-2">
                          <p className="font-bold mb-0.5">Remarks:</p>
                          <p className="mb-1">Please note that all Cheque/Draft will be in favor of <strong>'Aabkari Times'</strong> payable at Prayagraj with your artwork via e-mail to info@aabkaritimes.com</p>
                          
                          <p className="mt-1"><strong>Our Banker Name:</strong> Bank of Baroda, Beli Road Branch, Allahabad</p>
                          <p><strong>Current Account:</strong> 70020200000558</p>
                          <p><strong>IFSC Code:</strong> BARB0(zero)VJBELI</p>
                          <p><strong>PAN No.:</strong> AIBPM4779R</p>
                          
                          <div className="mt-2">
                            <p className="font-bold">Thanks & Best Regards,</p>
                            <p className="font-bold">Aabkari Times magazine</p>
                            <p>A Monthly News Magazine on Excise & Alco-Bev</p>
                          </div>
                        </div>
                        
                        <div className="w-1/2 pl-2">
                          <p className="font-bold mb-0.5">Terms & Conditions:</p>
                          {bill.terms_conditions ? (
                            <ul className="list-disc ml-4 space-y-0">
                              {bill.terms_conditions.split('\n').map((term, idx) => (
                                <li key={idx}>{term.replace(/^\d+\.\s*/, '')}</li>
                              ))}
                            </ul>
                          ) : (
                            <ul className="list-disc ml-4 space-y-0">
                              <li>Artwork approval required before printing for multiple issues.</li>
                              <li>Post-order confirmation, PO with full terms should be provided.</li>
                              <li>Dimensions: Full Page Size of 7.25" x 10.5" (A4)</li>
                              <li>Supported file formats: .jpeg, .jpg, .pdf, .cdr (CDR v.17 or below)</li>
                              <li>1 print copy shall be sent at your registered postal address.</li>
                            </ul>
                          )}
                          
                          <div className="mt-2 text-center">
                            {withSignature && <img src={aabkariSignatureUrl} alt="Aabkari Times Signature" style={{ width: '80px', height: '80px', objectFit: 'contain', margin: '0 auto' }} />}
                            <p className="mt-0.5 font-semibold text-xs">For Aabkari Times Magazine</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  bill.gst_no ? (
                    // Sugar Times GST Bill Format
                    <div className="bill-content" style={{ position: 'relative', zIndex: 1 }}>
                      <div className="header mb-2" style={{ minHeight: '150px' }}>
                        {withLetterhead && (
                          <img src={sugarLetterheadUrl} alt="Sugar Times Letterhead" style={{ width: '100%', maxHeight: '150px', objectFit: 'contain' }} />
                        )}
                      </div>
                      
                      <div className="invoice-header text-center py-1 mb-2" style={{ borderTop: '3px solid #16a34a', borderBottom: '3px solid #16a34a' }}>
                        <h2 className="text-base font-bold">
                          {bill.document_type === 'Proforma Invoice' ? 'PROFORMA INVOICE' : 
                           bill.document_type === 'Receipt' ? 'RECEIPT' : 
                           'INVOICE/CASH MEMO'}
                        </h2>
                      </div>
                      
                      <div className="flex justify-end mb-2 text-sm">
                        <div className="text-right">
                          <p className="mb-1">Date: <strong>{bill.bill_date ? new Date(bill.bill_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : new Date(bill.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong></p>
                          <p>{bill.document_type === 'Proforma Invoice' ? 'Proforma Invoice No' : 'Bill No'}: <strong>{bill.document_type === 'Proforma Invoice' ? (bill.proforma_invoice_no || bill.bill_no) : String(bill.bill_no).replace('.0', '')}</strong></p>
                          {bill.po_wo_no && <p className="mt-1">P.O./W.O. No: <strong>{bill.po_wo_no}</strong></p>}
                          {bill.document_type !== 'Proforma Invoice' && bill.proforma_invoice_no && <p className="mt-1">Proforma Invoice No: <strong>{bill.proforma_invoice_no}</strong></p>}
                        </div>
                      </div>
                      
                      <div className="mb-2 text-sm">
                        <p className="mb-1"><strong>To,</strong></p>
                        <p className="text-sm"><strong>{bill.initial} {bill.bill_receipt_name || bill.billing_name}</strong></p>
                        {bill.billing_address && <p>{bill.billing_address}</p>}
                        <p>{bill.district ? `${bill.district}` : ''}{bill.pincode ? `-${String(bill.pincode).replace('.0', '')}` : ''}{bill.billing_state ? ` (${bill.billing_state})` : ''}</p>
                        {bill.gst_no && <p className="mt-0.5"><strong>GST No.:</strong> {bill.gst_no}</p>}
                      </div>
                      
                      <table className="w-full border-collapse mb-2 text-sm" style={{ border: '1px solid #6b7280' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#16a34a', color: 'white' }}>
                            <th className="p-1.5 text-left" style={{ width: '40px', border: '1px solid #6b7280', fontSize: '10px' }}>S.No</th>
                            <th className="p-1.5 text-left" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>Description of Goods/Services</th>
                            <th className="p-1.5 text-right" style={{ width: '120px', border: '1px solid #6b7280', fontSize: '10px' }}>Amount(in Rs.)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="p-1.5 align-top" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>1.</td>
                            <td className="p-1.5" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>
                              <p className="mb-1">
                                {bill.item_description || `Towards your ${bill.product === 'Renewal' ? 'Subscription Renewal' : bill.product || 'Subscription'} charges of ${bill.magazine} magazine for ${bill.advertisement_tenure || '1 Month'}${bill.occasion && bill.occasion !== 'None' ? ` in occasion ${bill.occasion}` : ''}`}
                                {bill.include_in_aabkari && <span className="font-semibold"> + Aabkari Times (Complimentary)</span>}
                              </p>
                              <p className="mt-1.5 mb-0.5"><em style={{ fontSize: '9px' }}>Details-</em></p>
                              <p className="ml-2" style={{ fontSize: '9px' }}>Type- {bill.advertisement_position || bill.product}</p>
                              {bill.advertisement_tenure && <p className="ml-2" style={{ fontSize: '9px' }}>Period- {bill.advertisement_tenure}</p>}
                              {bill.start_month && bill.start_year && <p className="ml-2" style={{ fontSize: '9px' }}>Starts from- {bill.start_month} {bill.start_year}</p>}
                              {bill.end_month && bill.end_year && <p className="ml-2" style={{ fontSize: '9px' }}>Ends on- {bill.end_month} {bill.end_year}</p>}
                            </td>
                            <td className="p-1.5 text-right align-top font-semibold" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>{bill.gross_amount.toFixed(2)}</td>
                          </tr>
                          {/* Discount Row - Only shown if discount exists */}
                          {bill.discount_amount > 0 && (
                            <tr>
                              <td colSpan="2" className="p-1.5 text-right" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>
                                <span className="font-semibold">Less: Discount {bill.discount_percentage > 0 ? `(${bill.discount_percentage}%)` : ''}</span>
                              </td>
                              <td className="p-1.5 text-right font-semibold" style={{ border: '1px solid #6b7280', fontSize: '10px', color: '#dc2626' }}>
                                - {bill.discount_amount.toFixed(2)}
                              </td>
                            </tr>
                          )}
                          {bill.discount_amount > 0 && (
                            <tr>
                              <td colSpan="2" className="p-1.5 text-right" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>
                                <span className="font-semibold">Net Amount</span>
                              </td>
                              <td className="p-1.5 text-right font-semibold" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>
                                {(bill.gross_amount - (bill.discount_amount || 0)).toFixed(2)}
                              </td>
                            </tr>
                          )}
                          {bill.cgst > 0 && (
                            <tr>
                              <td colSpan="2" className="p-1.5 text-right" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>CGST @ 9%</td>
                              <td className="p-1.5 text-right" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>{bill.cgst.toFixed(2)}</td>
                            </tr>
                          )}
                          {bill.sgst > 0 && (
                            <tr>
                              <td colSpan="2" className="p-1.5 text-right" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>SGST @ 9%</td>
                              <td className="p-1.5 text-right" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>{bill.sgst.toFixed(2)}</td>
                            </tr>
                          )}
                          {bill.igst > 0 && (
                            <tr>
                              <td colSpan="2" className="p-1.5 text-right" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>IGST @ 18%</td>
                              <td className="p-1.5 text-right" style={{ border: '1px solid #6b7280', fontSize: '10px' }}>{bill.igst.toFixed(2)}</td>
                            </tr>
                          )}
                          <tr>
                            <td colSpan="2" className="p-1.5 text-right font-bold" style={{ border: '1px solid #6b7280', fontSize: '11px' }}>Total</td>
                            <td className="p-1.5 text-right font-bold" style={{ border: '1px solid #6b7280', fontSize: '12px' }}>₹ {bill.total_amount.toFixed(2)} /-</td>
                          </tr>
                        </tbody>
                      </table>
                      
                      <p className="text-sm mb-2"><strong>In words:</strong> <span className="italic">{numberToWords(bill.total_amount)}</span></p>
                      
                      <div className="footer text-sm mt-2">
                        <div className="flex justify-between">
                          <div className="w-1/2 pr-2">
                            <p className="font-bold mb-0.5" style={{ fontSize: '9px' }}>Remarks:</p>
                            <p className="mb-1" style={{ fontSize: '9px' }}>Please note that all Cheque/Draft will be in favor of <strong>'Sugar Times'</strong> payable at Prayagraj with your artwork via e-mail to info@sugartimes.co.in</p>
                            
                            <p className="mt-1" style={{ fontSize: '9px' }}><strong>Our Banker Name:</strong> UCO Bank, Mumfordganj Branch, Prayagraj</p>
                            <p style={{ fontSize: '9px' }}><strong>Current Account:</strong> 16110210000861</p>
                            <p style={{ fontSize: '9px' }}><strong>IFSC Code:</strong> UCBA0001611</p>
                            <p style={{ fontSize: '9px' }}><strong>PAN No.:</strong> CKHPM7476B</p>
                            
                            <div className="mt-2">
                              <p className="font-bold" style={{ fontSize: '9px' }}>Thanks & Best Regards,</p>
                              <p className="font-bold" style={{ fontSize: '10px' }}>Sugar Times magazine</p>
                              <p style={{ fontSize: '9px' }}>A Monthly News Magazine on Sugar, Ethanol & Bio-Energy</p>
                            </div>
                          </div>
                          
                          <div className="w-1/2 pl-2">
                            <p className="font-bold mb-0.5" style={{ fontSize: '9px' }}>Terms & Conditions:</p>
                            <ul className="list-disc ml-4 space-y-0" style={{ fontSize: '9px' }}>
                              <li>Artwork approval required before printing for multiple issues.</li>
                              <li>Post-order confirmation, PO with full terms should be provided.</li>
                              <li>Dimensions: Full Page Size of 7.25" x 10.5" (A4)</li>
                              <li>Supported file formats: .jpeg, .jpg, .pdf, .cdr (CDR v.17 or below)</li>
                              <li>1 print copy shall be sent at your registered postal address.</li>
                            </ul>
                            
                            <div className="mt-2 text-center">
                              {withSignature && <img src={sugarSignatureUrl} alt="Sugar Times Signature" style={{ width: '80px', height: '80px', objectFit: 'contain', margin: '0 auto' }} />}
                              <p className="mt-0.5 font-semibold" style={{ fontSize: '9px' }}>For Sugar Times Magazine</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Sugar Times Non-GST Bill Format
                    <div className="bill-content" style={{ position: 'relative', zIndex: 1 }}>
                      <div className="header mb-6" style={{ minHeight: '150px' }}>
                        {withLetterhead && (
                          <img src={sugarLetterheadUrl} alt="Sugar Times Letterhead" style={{ width: '100%', maxHeight: '150px', objectFit: 'contain' }} />
                        )}
                      </div>
                      
                      <div className="invoice-header text-center py-3 mb-5" style={{ borderTop: '3px solid #16a34a', borderBottom: '3px solid #16a34a' }}>
                        <h2 className="text-xl font-bold">{bill.document_type === 'Proforma Invoice' ? 'PROFORMA INVOICE' : 'INVOICE/CASH MEMO'}</h2>
                      </div>
                      
                      <div className="flex justify-end mb-5 text-sm">
                        <div className="text-right">
                          <p className="mb-1">Date: <strong>{bill.bill_date ? new Date(bill.bill_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : new Date(bill.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong></p>
                          <p>{bill.document_type === 'Proforma Invoice' ? 'Proforma Invoice No' : 'Bill No'}: <strong>{bill.document_type === 'Proforma Invoice' ? (bill.proforma_invoice_no || bill.bill_no) : String(bill.bill_no).replace('.0', '')}</strong></p>
                        </div>
                      </div>
                      
                      <div className="mb-5 text-sm">
                        <p className="mb-2"><strong>To,</strong></p>
                        <p className="text-base"><strong>{bill.initial} {bill.billing_name}</strong></p>
                        <p>{bill.district ? `${bill.district}` : ''}{bill.pincode ? `-${String(bill.pincode).replace('.0', '')}` : ''}{bill.billing_state ? ` (${bill.billing_state})` : ''}</p>
                        {bill.gst_no && <p className="mt-1"><strong>GST No.:</strong> {bill.gst_no}</p>}
                      </div>
                      
                      <table className="w-full border-collapse mb-4 text-sm" style={{ border: '1px solid #6b7280' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#c6f6d5', color: '#065f46' }}>
                            <th className="p-3 text-left" style={{ width: '60px', border: '1px solid #6b7280' }}>S.No</th>
                            <th className="p-3 text-left" style={{ border: '1px solid #6b7280' }}>Item</th>
                            <th className="p-3 text-right" style={{ width: '150px', border: '1px solid #6b7280' }}>Amount(in Rs.)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="p-3 align-top" style={{ border: '1px solid #6b7280' }}>1.</td>
                            <td className="p-3" style={{ border: '1px solid #6b7280' }}>
                              <p className="mb-2">
                                {bill.item_description || `Towards your ${bill.product === 'Renewal' ? 'Subscription Renewal' : bill.product || 'Subscription'} charges of ${bill.magazine} magazine for ${bill.advertisement_tenure || '1 Month'}${bill.occasion && bill.occasion !== 'None' ? ` in occasion ${bill.occasion}` : ''}`}
                                {bill.include_in_aabkari && <span className="font-semibold"> + Aabkari Times (Complimentary)</span>}
                              </p>
                              <p className="mt-3 mb-1"><em>Details-</em></p>
                              <p className="ml-2">Type- {bill.advertisement_position || bill.product}</p>
                              {bill.advertisement_tenure && <p className="ml-2">Period- {bill.advertisement_tenure}</p>}
                              {bill.start_month && bill.start_year && <p className="ml-2">Starts from- {bill.start_month} {bill.start_year}</p>}
                              {bill.end_month && bill.end_year && <p className="ml-2">Ends on- {bill.end_month} {bill.end_year}</p>}
                            </td>
                            <td className="p-3 text-right align-top font-semibold" style={{ border: '1px solid #6b7280' }}>{bill.total_amount.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td colSpan="2" className="p-3 text-right font-bold text-base" style={{ border: '1px solid #6b7280' }}>Total</td>
                            <td className="p-3 text-right font-bold text-lg" style={{ border: '1px solid #6b7280' }}>₹ {bill.total_amount.toFixed(2)} /-</td>
                          </tr>
                        </tbody>
                      </table>
                      
                      <p className="text-sm mb-5"><strong>In words:</strong> <span className="italic">{numberToWords(bill.total_amount)}</span></p>
                      
                      <div className="footer text-sm mt-8">
                        <div className="flex justify-between">
                          <div className="w-1/2 pr-2">
                            <p className="font-bold mb-0.5" style={{ fontSize: '9px' }}>Remarks:</p>
                            <p className="mb-1" style={{ fontSize: '9px' }}>Please note that all Cheque/Draft will be in favor of <strong>'Sugar Times'</strong> payable at Prayagraj with your artwork via e-mail to info@sugartimes.co.in</p>
                            
                            <p className="mt-1" style={{ fontSize: '9px' }}><strong>Our Banker Name:</strong> UCO Bank, Mumfordganj Branch, Prayagraj</p>
                            <p style={{ fontSize: '9px' }}><strong>Current Account:</strong> 16110210000861</p>
                            <p style={{ fontSize: '9px' }}><strong>IFSC Code:</strong> UCBA0001611</p>
                            <p style={{ fontSize: '9px' }}><strong>PAN No.:</strong> CKHPM7476B</p>
                            
                            <div className="mt-2">
                              <p className="font-bold" style={{ fontSize: '9px' }}>Thanks & Best Regards,</p>
                              <p className="font-bold" style={{ fontSize: '10px' }}>Sugar Times magazine</p>
                              <p style={{ fontSize: '9px' }}>A Monthly News Magazine on Sugar, Ethanol & Bio-Energy</p>
                            </div>
                          </div>
                          
                          <div className="w-1/2 pl-2">
                            <p className="font-bold mb-0.5" style={{ fontSize: '9px' }}>Terms & Conditions:</p>
                            <ul className="list-disc ml-4 space-y-0" style={{ fontSize: '9px' }}>
                              <li>Artwork approval required before printing for multiple issues.</li>
                              <li>Post-order confirmation, PO with full terms should be provided.</li>
                              <li>Dimensions: Full Page Size of 7.25" x 10.5" (A4)</li>
                              <li>Supported file formats: .jpeg, .jpg, .pdf, .cdr (CDR v.17 or below)</li>
                              <li>1 print copy shall be sent at your registered postal address.</li>
                            </ul>
                            
                            <div className="mt-2 text-center">
                              {withSignature && <img src={sugarSignatureUrl} alt="Sugar Times Signature" style={{ width: '80px', height: '80px', objectFit: 'contain', margin: '0 auto' }} />}
                              <p className="mt-0.5 font-semibold" style={{ fontSize: '9px' }}>For Sugar Times Magazine</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintBill;
